import type {
	CaptureInstanceFeedbackRequest,
	CaptureInstanceFeedbackResponse,
	ListInstanceFeedbackMineRequest,
	ListInstanceFeedbackMineResponse,
} from "@getuserfeedback/protocol";
import {
	type AppEventCapability,
	type AppEventFlag,
	assertNoSegmentExternalIdsInValueArgument,
	type CapabilitiesInput,
	type ClientMeta,
	type ClientOptions,
	type Command,
	type CommandEnvelope,
	CommandSettlementError,
	CommandSettlementTimeoutError,
	type ConfigureOptions,
	type ContainerPolicy,
	createCommandEnvelope,
	createCommandRequestId,
	createCommandSettlementDispatch,
	createFlowCommandHandleController,
	type FlagsInput,
	getFeedbackReadFromSettlementDetail,
	getFeedbackSubmissionFromSettlementDetail,
	getFlowHandleFromSettlementDetail,
	type FlowRenderState as HostFlowRenderState,
	type IdentifyOptions,
	type InitOptions,
	type LinkRouter,
	LOADER_RUNTIME_GLOBAL_KEY,
	LOADER_RUNTIME_PROTOCOL_VERSION,
	OPEN_URL_ACTION_CAPABILITY,
	OPEN_URL_ACTION_V1_CAPABILITY,
	OPEN_URL_ACTION_VERSION,
	type ResponseMetadataInput,
	SDK_PROTOCOL_CAPABILITIES,
	type WidgetLayoutTransitionPolicy,
	waitForCommandSettlement,
} from "@getuserfeedback/protocol/host";
import { UNIFIED_FLOW_RUNTIME_V1_CAPABILITY } from "@getuserfeedback/protocol/internal/core-host-command-settlement";
import { suppliedOnlyLocalInitOptionsSchema } from "@getuserfeedback/protocol/internal/core-init-options";
import type { InternalHostCommandEnvelope } from "@getuserfeedback/protocol/internal/host-command-envelope";
import type {
	InternalHostInlineFlowCommandPayload,
	InternalHostSuppliedOnlyLocalInitCommandPayload,
} from "@getuserfeedback/protocol/internal/host-command-payload";
import type { InlineFlow } from "@getuserfeedback/protocol/internal/inline-flow";
import { parseCustomerOccurrenceName } from "@getuserfeedback/protocol/internal/sdk-event-options";
import { createClientTransportAdmission } from "./internal/client-transport-admission";
import {
	buildCustomerOccurrenceCommand,
	type EventOptions,
	type EventProperties,
} from "./internal/customer-occurrence-command";
import { createFlowOpenRequestController } from "./internal/flow-open-request-controller";
import type { InlineFlowRun } from "./internal/inline-flow";
import { attachInlineFlowClientBridge } from "./internal/inline-flow-client-bridge";
import { invokeLinkRouter, resolveLinkRouter } from "./internal/link-router";
import { SDK_VERSION } from "./version";

export type {
	EventOptions,
	EventProperties,
} from "./internal/customer-occurrence-command";
export type { ClientOptions };

/** Feedback captured as an evaluation of the current app or a specific target. */
export type FeedbackInput = CaptureInstanceFeedbackRequest;

/** Receipt returned after feedback has been accepted. */
export type SubmitFeedbackResult = CaptureInstanceFeedbackResponse;

/** Selectors used to read the current authenticated user's feedback. */
export type FetchFeedbackInput = ListInstanceFeedbackMineRequest;

/** Feedback matching each requested selector, in selector order. */
export type FetchFeedbackResult = ListInstanceFeedbackMineResponse;

/** Operations for submitting and reading feedback through the current SDK client. */
export type FeedbackOperations = {
	/**
	 * Read feedback matching each selector for the JWT-authenticated user.
	 * Requires `auth.jwt` configuration; `identify()` alone does not authorize readback.
	 */
	fetch: (input: FetchFeedbackInput) => Promise<FetchFeedbackResult>;
	submit: (input: FeedbackInput) => Promise<SubmitFeedbackResult>;
};

/** One endpoint in a directed relationship observation. */
export type GraphRelationshipRef = {
	collection: string;
	id: string;
};

/** A directed relationship observation recorded through the analytics pipeline. */
export type GraphRelationship = {
	from: GraphRelationshipRef;
	to: GraphRelationshipRef;
};

/** Operations for recording relationship observations as product events. */
export type GraphOperations = {
	connect: (relationship: GraphRelationship) => Promise<void>;
	disconnect: (relationship: GraphRelationship) => Promise<void>;
};

import { type ActionRegistry, createActionRegistry } from "./action-registry";
import { createClientHostEventHub } from "./client-host-event-hub";
import { createOpenRequestBridge } from "./open-request-bridge";
import type { OpenRequestedEvent as OpenRequestDispatcherEvent } from "./open-request-dispatcher";
import { resolveStartupRuntimeUrls } from "./runtime-url-defaults";

/** @internal */
declare global {
	interface Window {
		__getuserfeedback_queue?: Array<
			CommandEnvelope | InternalHostCommandEnvelope
		>;
		[LOADER_RUNTIME_GLOBAL_KEY]?: {
			alive?: boolean;
			protocolVersion?: string;
			features?: readonly string[];
		};
	}
}

type FlowHandleId = string;

type FlowRunTarget = { flowId: string } | { flow: InlineFlow };

/** Durable state of a flow run (open/loading + dimensions when known). */
export interface FlowState extends HostFlowRenderState {
	/**
	 * @deprecated Stop reading this field. SDK state snapshots and subscriptions
	 * never populate it, and there is no replacement SDK state field.
	 */
	transitionPolicy?: WidgetLayoutTransitionPolicy;
}

/** Callback invoked when flow state changes. */
export type FlowStateCallback = (state: FlowState) => void;
type IdentifyTraits = Record<string, unknown>;
export type OpenRequestedEvent = OpenRequestDispatcherEvent;
export type OpenRequestedCallback = (event: OpenRequestedEvent) => void;
type StableJsonObject = { [key: string]: StableJsonValue };
type StableJsonValue =
	| string
	| number
	| boolean
	| null
	| StableJsonValue[]
	| StableJsonObject;

/** Options for opening a flow run. */
export type OpenFlowOptions = {
	/** Host container used for this open request. Omit to use default floating mounting; pass null to wait for a custom container. */
	container?: HTMLElement | null;
	/**
	 * Optional response metadata to preserve with the eventual submission.
	 * These tags are attached when the flow is opened and do not affect prerendering.
	 */
	metadata?: ResponseMetadataInput;
	/**
	 * When `true`, the flow view does not show a close button.
	 * Useful for mobile or embedded contexts where the host handles dismissal (e.g. via a drawer or back gesture).
	 */
	hideCloseButton?: boolean;
};

/** Options for prerendering a flow run. */
export type PrerenderFlowOptions = {
	/**
	 * When `true`, the prerendered view does not show a close button.
	 */
	hideCloseButton?: boolean;
};

/** Options for subscribing to flow state updates. */
export type SubscribeFlowStateOptions = {
	/** Emit the current snapshot immediately. Default `true`. */
	emitInitial?: boolean;
	/** Auto-unsubscribe when the signal is aborted. */
	signal?: AbortSignal;
};

const DEFAULT_FLOW_STATE: FlowState = {
	isOpen: false,
	isLoading: false,
	width: undefined,
	height: undefined,
};
const STALE_HANDLE_ERROR_CODE = "STALE_HANDLE" as const;

const isStaleHandleError = (error: unknown): boolean => {
	if (error instanceof CommandSettlementError) {
		return error.code === STALE_HANDLE_ERROR_CODE;
	}
	if (!(error instanceof Error)) {
		return false;
	}
	return error.message.includes("flowHandleId is stale");
};

/** Instance for a specific flow: open, prefetch, prerender, close, and subscribe to flow state. */
export type FlowRun = {
	/** The flow/survey ID this instance refers to. */
	flowId: string;
	/** Open the flow (show the survey). */
	open: (options?: OpenFlowOptions) => Promise<void>;
	/** Prefetch the flow so it opens faster when you call `open()`. */
	prefetch: () => Promise<void>;
	/** Prerender the flow into a container (e.g. for a custom dialog). */
	prerender: (options?: PrerenderFlowOptions) => Promise<void>;
	/** Attach or detach this flow to a specific host container. */
	setContainer: (element: HTMLElement | null) => void;
	/** Close this flow. */
	close: () => Promise<void>;
	/** Return the latest known flow state snapshot. */
	getFlowState: () => FlowState;
	/** Subscribe to open/loading/dimensions; returns an unsubscribe function. */
	subscribeFlowState: (
		callback: FlowStateCallback,
		options?: SubscribeFlowStateOptions,
	) => () => void;
};

/**
 * getuserfeedback client for loading the widget, opening flows, configuring
 * consent/auth, identifying users, and tracking events.
 *
 * @see https://getuserfeedback.com/docs/reference/javascript-sdk-reference
 */
export type Client = {
	/** Load the widget script. Call when you created the client with `disableAutoLoad: true`. */
	load: () => void;
	/** Set how this instance should choose default containers for flows. */
	setDefaultContainerPolicy: (policy: ContainerPolicy) => void;
	/** Return the latest known instance-level flow state snapshot. */
	getFlowState: () => FlowState;
	/** Subscribe to instance-level flow state updates (eg targeting-triggered opens). */
	subscribeFlowState: (
		callback: FlowStateCallback,
		options?: SubscribeFlowStateOptions,
	) => () => void;
	/** Return a flow instance; call .open(), .prefetch(), .prerender(), .close() on it. */
	flow: (flowId: string) => FlowRun;
	/** Close all open flows. */
	close: () => Promise<void>;
	/** Close all open flows, clear JWT authentication, and reset user identity. */
	reset: () => Promise<void>;
	/**
	 * Update color scheme, consent, or auth. Pass a fresh JWT here after login
	 * and whenever your auth provider refreshes the session token. Await the
	 * returned promise or handle its rejection when applying runtime updates.
	 *
	 * When using `disableAutoLoad`, call `load()` first. Calls made before `load()`
	 * resolve without applying the update. Provide the initial color scheme,
	 * default consent, and capabilities to `createClient()`; configure auth after
	 * `load()`.
	 *
	 * @see https://getuserfeedback.com/docs/security-and-privacy/jwt-auth
	 */
	configure: (updates: ConfigureOptions) => Promise<void>;
	/**
	 * Associate the current user with a user ID, traits, and optional external IDs.
	 *
	 * Use `options.externalIds` for Segment-compatible identifiers from another
	 * system. Without a user ID, pass options as `client.identify(traits, options)`,
	 * or use `client.identify(traits, undefined, options)` when your call site needs
	 * the explicit three-argument form. Do not put Segment-shaped `externalIds` in
	 * traits. Some invalid arguments throw synchronously; other validation or
	 * command-processing failures reject the returned promise.
	 *
	 * @see https://getuserfeedback.com/docs/guides/identity-resolution
	 */
	identify: {
		(
			userId: string,
			traits?: IdentifyTraits,
			options?: IdentifyOptions,
		): Promise<void>;
		(
			traits: IdentifyTraits,
			placeholder: undefined,
			options?: IdentifyOptions,
		): Promise<void>;
		(traits: IdentifyTraits, options?: IdentifyOptions): Promise<void>;
	};
	/**
	 * Track a product event from the browser.
	 *
	 * Use `options.externalIds` when the event carries a Segment-compatible
	 * identifier from another system. Do not put Segment-shaped `externalIds` in
	 * properties. Some invalid arguments throw synchronously; other validation or
	 * command-processing failures reject the returned promise.
	 *
	 * @see https://getuserfeedback.com/docs/reference/events
	 */
	track: (
		eventName: string,
		properties?: EventProperties,
		options?: EventOptions,
	) => Promise<void>;
	/**
	 * Record the current browser page or a named page location. Some invalid
	 * arguments throw synchronously; other validation or command-processing
	 * failures reject the returned promise.
	 */
	page: (
		name?: string,
		properties?: EventProperties,
		options?: EventOptions,
	) => Promise<void>;
	/** Record directed relationship observations through the analytics pipeline. */
	graph: GraphOperations;
	/** Submit feedback or read the authenticated user's matching feedback. */
	feedback: FeedbackOperations;
	/** Subscribe to explicit SDK and client-side targeting open requests, or suppress them — for example if you know the user should not be interrupted.
	 * For example, when the user is filling out a form, or is in the middle of a multi-step flow.
	 * Prevention applies only to that presentation attempt; it does not create a
	 * public retry schedule.
	 *
	 * @example — suppress open flow requests if the user is busy
	 * ```ts
	 * client.onOpenRequested((event) => {
	 *   if (userIsBusy()) {
	 *     event.preventDefault();
	 *   }
	 * });
	 * ```
	 */
	onOpenRequested: (callback: OpenRequestedCallback) => () => void;
};

type ClientRegistryEntry = {
	client: Client;
	updateOptions: (
		options: ClientOptions,
		actionRegistry: ActionRegistry,
		linkRouter: LinkRouter | undefined,
	) => void;
};

const openRequestBridgeByClient = new WeakMap<
	Client,
	ReturnType<typeof createOpenRequestBridge>
>();

const clientRegistry = new Map<object, Map<string, ClientRegistryEntry>>();

export const getOpenRequestBridge = (
	client: Client,
): ReturnType<typeof createOpenRequestBridge> => {
	const bridge = openRequestBridgeByClient.get(client);
	if (!bridge) {
		throw new Error("Open request bridge not found for client instance.");
	}
	return bridge;
};

const createClientInstanceId = (): string => {
	if (
		typeof globalThis.crypto !== "undefined" &&
		typeof globalThis.crypto.randomUUID === "function"
	) {
		return globalThis.crypto.randomUUID();
	}
	return `sdk-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const toIdentifyCommandPayload = (
	identifyInput: string | IdentifyTraits,
	traitsOrOptions?: IdentifyTraits | IdentifyOptions,
	options?: IdentifyOptions,
): Extract<Command, { kind: "identify" }> => {
	if (typeof identifyInput === "string") {
		assertNoSegmentExternalIdsInValueArgument({
			argument: traitsOrOptions,
			commandName: "identify",
			valueArgumentName: "traits",
			callShape: "identify(userId, traits, { externalIds })",
		});
		return {
			kind: "identify",
			userId: identifyInput,
			traits: traitsOrOptions,
			options,
		};
	}
	assertNoSegmentExternalIdsInValueArgument({
		argument: identifyInput,
		commandName: "identify",
		valueArgumentName: "traits",
		callShape: "identify(traits, { externalIds })",
	});
	return {
		kind: "identify",
		traits: identifyInput,
		options: options ?? traitsOrOptions,
	};
};

const toFlags = (input: FlagsInput | undefined): AppEventFlag[] | undefined => {
	if (input === undefined) {
		return;
	}
	if (Array.isArray(input)) {
		return input.map((flag) => ({ ...flag }));
	}
	return Object.entries(input)
		.sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
		.map(([key, value]) => ({ key, value }));
};

const toCapabilities = (
	input: CapabilitiesInput | undefined,
): AppEventCapability[] | undefined => {
	if (input === undefined) {
		return;
	}
	return input.map((capability) =>
		typeof capability === "string" ? { key: capability } : { ...capability },
	);
};

const CIRCULAR_TARGETING_CONTEXT_ERROR =
	"Targeting context cannot contain circular values";

const toStableJsonValue = (
	value: unknown,
	ancestors: WeakSet<object> = new WeakSet(),
): StableJsonValue => {
	if (
		value === null ||
		typeof value === "string" ||
		typeof value === "number" ||
		typeof value === "boolean"
	) {
		return value;
	}
	if (Array.isArray(value)) {
		if (ancestors.has(value)) {
			throw new Error(CIRCULAR_TARGETING_CONTEXT_ERROR);
		}
		ancestors.add(value);
		try {
			return value.map((entryValue) =>
				toStableJsonValue(entryValue, ancestors),
			);
		} finally {
			ancestors.delete(value);
		}
	}
	if (typeof value === "object") {
		if (ancestors.has(value)) {
			throw new Error(CIRCULAR_TARGETING_CONTEXT_ERROR);
		}
		ancestors.add(value);
		const objectValue: StableJsonObject = {};
		try {
			for (const [key, entryValue] of Object.entries(value).sort(
				([leftKey], [rightKey]) => leftKey.localeCompare(rightKey),
			)) {
				if (entryValue !== undefined) {
					objectValue[key] = toStableJsonValue(entryValue, ancestors);
				}
			}
			return objectValue;
		} finally {
			ancestors.delete(value);
		}
	}
	return null;
};

const toCanonicalTargetingValues = (
	values: unknown[] | undefined,
): StableJsonValue[] | null => {
	if (values === undefined || values.length === 0) {
		return null;
	}
	return values
		.map((value) => {
			const stableValue = toStableJsonValue(value);
			return { key: JSON.stringify(stableValue), value: stableValue };
		})
		.sort(({ key: leftKey }, { key: rightKey }) =>
			leftKey.localeCompare(rightKey),
		)
		.map(({ value }) => value);
};

const resolveTargetingContextRegistryKey = (options: ClientOptions): string =>
	JSON.stringify({
		flags: toCanonicalTargetingValues(toFlags(options.flags)),
		capabilities: toCanonicalTargetingValues(
			toCapabilities(options.capabilities),
		),
	});

const LOADER_RUNTIME_PROBE_TIMEOUT_MS = 120;
// Once Loader registration has been enqueued, it is ordinary command settlement
// rather than a short ownership probe. Keep supplied-flow handoff behind that
// settlement so Core capability negotiation can complete before admission.
const LOADER_RUNTIME_POST_LOAD_TIMEOUT_MS = 30_000;
const INVALID_COMMAND_ERROR_CODE = "INVALID_COMMAND";
const SUPPLIED_ONLY_LOCAL_CLIENT_OPTIONS_SYMBOL = Symbol.for(
	"@getuserfeedback/sdk/internal/supplied-only-local-client-options/1",
);
const BUNDLED_DOCS_LOCAL_API_KEY = "@getuserfeedback/internal/bundled-docs";

type MarkedSuppliedOnlyLocalClientOptions = ClientOptions & {
	[SUPPLIED_ONLY_LOCAL_CLIENT_OPTIONS_SYMBOL]: true;
};
type MaybeMarkedSuppliedOnlyLocalClientOptions = ClientOptions & {
	[SUPPLIED_ONLY_LOCAL_CLIENT_OPTIONS_SYMBOL]?: true;
};

/** Private runtime inputs for the deployment-owned bundled-docs client. */
export type BundledDocsClientOptions = {
	loaderUrl: string;
	coreUrl: string;
	colorScheme?: ClientOptions["colorScheme"];
	disableAutoLoad?: boolean;
};

/**
 * Build options that the public React provider can pass through to the SDK root
 * without exposing local registration in the public options type.
 * Pass the returned object intact; cloning or serializing it deliberately fails
 * closed instead of falling back to persisted registration.
 * See docs/specs/2026-08-02-flow-interaction-model.md.
 */
export const createBundledDocsClientOptions = (
	options: BundledDocsClientOptions,
): ClientOptions => {
	const clientOptions: ClientOptions = {
		apiKey: BUNDLED_DOCS_LOCAL_API_KEY,
		_loaderUrl: options.loaderUrl,
		_coreUrl: options.coreUrl,
		...(options.colorScheme === undefined
			? {}
			: { colorScheme: options.colorScheme }),
		...(options.disableAutoLoad === undefined
			? {}
			: { disableAutoLoad: options.disableAutoLoad }),
	};
	Object.defineProperty(
		clientOptions,
		SUPPLIED_ONLY_LOCAL_CLIENT_OPTIONS_SYMBOL,
		{
			configurable: false,
			enumerable: false,
			value: true,
			writable: false,
		},
	);
	return clientOptions;
};

const isSuppliedOnlyLocalClientOptions = (
	options: MaybeMarkedSuppliedOnlyLocalClientOptions,
): options is MarkedSuppliedOnlyLocalClientOptions =>
	options[SUPPLIED_ONLY_LOCAL_CLIENT_OPTIONS_SYMBOL] === true;

const resolveRegistryKey = (
	options: ClientOptions,
	registrationKind: "persisted" | "supplied-only-local",
): string =>
	`${registrationKind}:${options.apiKey}:${resolveTargetingContextRegistryKey(options)}`;

const haveSameActionDefinitions = (left: string[], right: string[]): boolean =>
	JSON.stringify(left) === JSON.stringify(right);

const rejectAmbiguousLinkOwnership = (
	linkRouter: LinkRouter | undefined,
	actionRegistry: ActionRegistry,
): void => {
	if (linkRouter !== undefined && actionRegistry.getOpenUrlHandler()) {
		throw new TypeError(
			"links.router cannot be configured with actions open-url; choose one link owner",
		);
	}
};

const resolveRegistryScope = (): object =>
	typeof window === "undefined" ? globalThis : window;

const getRegistryForScope = (): Map<string, ClientRegistryEntry> => {
	const scope = resolveRegistryScope();
	const existing = clientRegistry.get(scope);
	if (existing) {
		return existing;
	}
	const created = new Map<string, ClientRegistryEntry>();
	clientRegistry.set(scope, created);
	return created;
};

/**
 * Create a getuserfeedback client. Reuses an existing logical client for the
 * same `apiKey` and targeting context. Host-action definitions may change
 * before loading starts; afterward, changing their canonical identities throws
 * while equivalent definitions refresh their handlers.
 */
export const createClient = (options: ClientOptions): Client => {
	const suppliedOnlyLocal = isSuppliedOnlyLocalClientOptions(options);
	if (!suppliedOnlyLocal && options.apiKey === BUNDLED_DOCS_LOCAL_API_KEY) {
		throw new Error(
			"Bundled docs client options must be passed intact without cloning or serialization.",
		);
	}
	const {
		apiKey,
		_loaderUrl,
		_coreUrl,
		_apiUrl,
		_realtimeUrl,
		disableAutoLoad = false,
		colorScheme,
		disableTelemetry = false,
		enableDebug = false,
		defaultConsent,
		flags,
		capabilities,
		actions,
		links,
	} = options;
	const initialActionRegistry = createActionRegistry(actions);
	const initialLinkRouter = resolveLinkRouter(links);
	rejectAmbiguousLinkOwnership(initialLinkRouter, initialActionRegistry);
	const registryKey = resolveRegistryKey(
		{
			apiKey,
			_loaderUrl,
			_coreUrl,
			_apiUrl,
			_realtimeUrl,
			disableAutoLoad,
			colorScheme,
			disableTelemetry,
			enableDebug,
			defaultConsent,
			flags,
			capabilities,
			actions,
		},
		suppliedOnlyLocal ? "supplied-only-local" : "persisted",
	);
	const registry = getRegistryForScope();
	const existing = registry.get(registryKey);
	if (existing) {
		existing.updateOptions(
			{
				apiKey,
				_loaderUrl,
				_coreUrl,
				_apiUrl,
				_realtimeUrl,
				disableAutoLoad,
				colorScheme,
				disableTelemetry,
				enableDebug,
				defaultConsent,
				flags,
				capabilities,
				actions,
			},
			initialActionRegistry,
			initialLinkRouter,
		);
		if (!disableAutoLoad) {
			existing.client.load();
		}
		return existing.client;
	}

	let loaderUrlConfig = _loaderUrl;
	let coreUrlConfig = _coreUrl;
	let apiUrlConfig = _apiUrl;
	let realtimeUrlConfig = _realtimeUrl;

	const notifyError = (_error: unknown): void => {
		// No-op: track/use hooks removed from public API.
	};
	const hostWindow =
		typeof window !== "undefined" &&
		typeof window.addEventListener === "function" &&
		typeof window.removeEventListener === "function"
			? window
			: null;
	const hostDocument =
		typeof document !== "undefined" &&
		typeof document.createElement === "function" &&
		document.head !== null
			? document
			: null;

	const sdkVersion = SDK_VERSION;
	const clientInstanceId = createClientInstanceId();
	const openRequestBridge = createOpenRequestBridge();
	const clientMeta: ClientMeta = {
		loader: "sdk",
		clientName: "@getuserfeedback/sdk",
		clientVersion: sdkVersion,
		transport: "snippet",
	};

	let defaultConsentConfig = defaultConsent;
	let colorSchemeConfig = colorScheme;
	let flagsConfig = toFlags(flags);
	let capabilitiesConfig = toCapabilities(capabilities);
	let actionRegistry = initialActionRegistry;
	let linkRouterConfig = initialLinkRouter;

	const buildRuntimeEndpoints = (): InitOptions["runtimeEndpoints"] => {
		if (!(coreUrlConfig || apiUrlConfig || realtimeUrlConfig)) {
			return undefined;
		}
		return {
			...(coreUrlConfig !== undefined ? { coreUrl: coreUrlConfig } : {}),
			...(apiUrlConfig !== undefined ? { apiUrl: apiUrlConfig } : {}),
			...(realtimeUrlConfig !== undefined
				? { realtimeUrl: realtimeUrlConfig }
				: {}),
		};
	};

	const buildInitOpts = (includeHostActions = true): InitOptions => {
		const runtimeEndpoints = buildRuntimeEndpoints();
		const hostActionDefinitions = includeHostActions
			? actionRegistry.listDefinitions()
			: [];
		const protocolCapabilities = [
			...SDK_PROTOCOL_CAPABILITIES,
			...(linkRouterConfig !== undefined || actionRegistry.getOpenUrlHandler()
				? [OPEN_URL_ACTION_CAPABILITY, OPEN_URL_ACTION_V1_CAPABILITY]
				: []),
		];
		return {
			apiKey,
			clientMeta,
			capabilities: [...protocolCapabilities],
			...(flagsConfig !== undefined && {
				flags: flagsConfig.map((flag) => ({ ...flag })),
			}),
			...(capabilitiesConfig !== undefined && {
				hostCapabilities: capabilitiesConfig.map((capability) => ({
					...capability,
				})),
			}),
			...(hostActionDefinitions.length > 0 && {
				hostActions: hostActionDefinitions,
			}),
			...(runtimeEndpoints !== undefined ? { runtimeEndpoints } : {}),
			...(colorSchemeConfig !== undefined && {
				colorScheme: colorSchemeConfig,
			}),
			...(disableTelemetry !== undefined && { disableTelemetry }),
			...(enableDebug !== undefined && { enableDebug }),
			...(defaultConsentConfig !== undefined && {
				defaultConsent: defaultConsentConfig,
			}),
		};
	};

	let loaderRequested = false;
	let pendingLoad: Promise<void> | null = null;
	let pendingRegistrationProbe: Promise<void> | null = null;
	let disableAutoLoadConfig = disableAutoLoad;
	const flowStateByHandle = new Map<FlowHandleId, FlowState>();
	const claimedFlowStateHandles = new Set<FlowHandleId>();
	const pendingFlowStateHandles = new Set<FlowHandleId>();
	let isFlowHandleAllocationActive = false;
	let isFlowHandleAllocationLocked = false;
	const flowHandleAllocationWaiters: Array<(release: () => void) => void> = [];
	let instanceFlowState: FlowState = DEFAULT_FLOW_STATE;
	const hostEventHub = createClientHostEventHub({
		instanceId: clientInstanceId,
		onNavigationRequested: (detail) => {
			const target =
				detail.version === 1 && detail.target === "_blank"
					? undefined
					: detail.version === OPEN_URL_ACTION_VERSION &&
							(detail.target === undefined ||
								detail.target === "self" ||
								detail.target === "blank")
						? detail.target
						: null;
			if (target === null) {
				return "unhandled";
			}
			if (linkRouterConfig !== undefined) {
				invokeLinkRouter(linkRouterConfig, {
					url: detail.url,
					...(target === undefined ? {} : { target }),
				});
				return "succeeded";
			}
			const handler = actionRegistry.getOpenUrlHandler();
			if (!handler) {
				return "unhandled";
			}
			const outcome = handler({
				url: detail.url,
				...(target === undefined ? {} : { target }),
			});
			if (outcome === "handled") {
				return "succeeded";
			}
			if (outcome === "unhandled") {
				return "unhandled";
			}
			throw new TypeError(
				'Registered open-url action must return "handled" or "unhandled" synchronously.',
			);
		},
		windowRef: hostWindow,
	});

	const resolveRequiredRuntimeUrls = (): {
		loaderUrl: string;
		coreUrl: string;
		apiUrl: string;
		realtimeUrl?: string;
	} => {
		const runtimeUrls = resolveStartupRuntimeUrls({
			apiKey,
			_loaderUrl: loaderUrlConfig,
			_coreUrl: coreUrlConfig,
			_apiUrl: apiUrlConfig,
			_realtimeUrl: realtimeUrlConfig,
		});
		loaderUrlConfig = runtimeUrls.loaderUrl;
		coreUrlConfig = runtimeUrls.coreUrl;
		apiUrlConfig = runtimeUrls.apiUrl;
		realtimeUrlConfig = runtimeUrls.realtimeUrl;
		return runtimeUrls;
	};

	const cloneFlowState = (state: FlowState): FlowState => ({
		isOpen: state.isOpen,
		isLoading: state.isLoading,
		width: state.width,
		height: state.height,
	});

	const getFlowStateForHandle = (flowHandleId: FlowHandleId): FlowState => {
		const existing = flowStateByHandle.get(flowHandleId);
		if (!existing) {
			return cloneFlowState(DEFAULT_FLOW_STATE);
		}
		return cloneFlowState(existing);
	};

	const setFlowStateForHandle = (
		flowHandleId: FlowHandleId,
		state: FlowState,
	): void => {
		flowStateByHandle.set(flowHandleId, cloneFlowState(state));
	};

	// Core can publish state for a newly allocated supplied Flow before the
	// command settlement tells its FlowRun which handle was allocated. Keep the
	// client-owned snapshot current so binding that handle can replay the state
	// instead of losing the first open transition.
	hostEventHub.subscribeFlowState((detail): void => {
		const isClaimedHandle = claimedFlowStateHandles.has(detail.flowHandleId);
		if (!isClaimedHandle && !isFlowHandleAllocationActive) {
			return;
		}
		setFlowStateForHandle(detail.flowHandleId, {
			isOpen: detail.isOpen,
			isLoading: detail.isLoading,
			width: detail.width,
			height: detail.height,
		});
		if (!isClaimedHandle) {
			pendingFlowStateHandles.add(detail.flowHandleId);
		}
	});
	const beginFlowHandleAllocation = (): void => {
		isFlowHandleAllocationActive = true;
	};
	const clearPendingFlowHandleState = (): void => {
		for (const flowHandleId of pendingFlowStateHandles) {
			flowStateByHandle.delete(flowHandleId);
		}
		pendingFlowStateHandles.clear();
	};
	const endFlowHandleAllocation = (): void => {
		isFlowHandleAllocationActive = false;
		clearPendingFlowHandleState();
	};
	/**
	 * Flow-state events do not carry the allocating command requestId. Serialize
	 * only handle-free handoffs so a pre-settlement snapshot has one possible
	 * owner. Other commands remain concurrent. If allocation concurrency becomes
	 * a product requirement, add explicit event correlation instead of a ledger.
	 */
	const createFlowHandleAllocationRelease = (): (() => void) => {
		let released = false;
		return () => {
			if (released) {
				return;
			}
			released = true;
			const next = flowHandleAllocationWaiters.shift();
			if (next) {
				next(createFlowHandleAllocationRelease());
				return;
			}
			isFlowHandleAllocationLocked = false;
		};
	};
	const acquireFlowHandleAllocation = ():
		| (() => void)
		| Promise<() => void> => {
		if (!isFlowHandleAllocationLocked) {
			isFlowHandleAllocationLocked = true;
			return createFlowHandleAllocationRelease();
		}
		return new Promise<() => void>((resolve) => {
			flowHandleAllocationWaiters.push(resolve);
		});
	};

	const getInstanceFlowState = (): FlowState =>
		cloneFlowState(instanceFlowState);

	const setInstanceFlowState = (state: FlowState): void => {
		instanceFlowState = cloneFlowState(state);
	};

	hostEventHub.subscribeInstanceFlowState((detail): void => {
		setInstanceFlowState({
			isOpen: detail.isOpen,
			isLoading: detail.isLoading,
			width: detail.width,
			height: detail.height,
		});
	});

	const ensureFlowStateForHandle = (flowHandleId: FlowHandleId): void => {
		if (!flowStateByHandle.has(flowHandleId)) {
			setFlowStateForHandle(flowHandleId, DEFAULT_FLOW_STATE);
		}
	};

	const subscribeFlowHandleInvalidation = (
		subscriber: (flowHandleId: FlowHandleId) => void,
	): (() => void) => hostEventHub.subscribeFlowHandleInvalidation(subscriber);

	const subscribeFlowStateCacheForHandle = (
		flowHandleId: FlowHandleId,
	): (() => void) =>
		hostEventHub.subscribeFlowStateForHandle(flowHandleId, (detail): void => {
			const nextState: FlowState = {
				isOpen: detail.isOpen,
				isLoading: detail.isLoading,
				width: detail.width,
				height: detail.height,
			};
			setFlowStateForHandle(flowHandleId, nextState);
		});

	const isLoaderRequested = (): boolean => loaderRequested;

	const enqueue = (
		envelope: CommandEnvelope | InternalHostCommandEnvelope,
	): void => {
		if (!hostWindow) {
			return;
		}
		if (!hostWindow.__getuserfeedback_queue) {
			hostWindow.__getuserfeedback_queue = [];
		}
		// Stamp instanceId and clientMeta so the loader can route commands.
		const stamped: CommandEnvelope | InternalHostCommandEnvelope = {
			...envelope,
			instanceId: envelope.instanceId ?? clientInstanceId,
			clientMeta: envelope.clientMeta ?? clientMeta,
		};
		hostWindow.__getuserfeedback_queue.push(stamped);
	};

	const enqueueInstanceRegistration = (
		requestId?: string,
		includeHostActions = true,
	): string => {
		if (suppliedOnlyLocal) {
			if (!coreUrlConfig) {
				throw new Error(
					"_coreUrl is not resolved for bundled docs registration",
				);
			}
			const localRequestId = requestId ?? createCommandRequestId();
			const command: InternalHostSuppliedOnlyLocalInitCommandPayload = {
				kind: "init",
				opts: suppliedOnlyLocalInitOptionsSchema.parse({
					registration: {
						kind: "supplied-only-local",
						profile: "bundled-docs",
					},
					...(colorSchemeConfig === undefined
						? {}
						: { colorScheme: colorSchemeConfig }),
				}),
				runtimeEndpoints: { coreUrl: coreUrlConfig },
			};
			enqueue({
				version: "1",
				requestId: localRequestId,
				idempotencyKey: localRequestId,
				command,
			});
			return localRequestId;
		}
		const envelope = createCommandEnvelope({
			instanceId: clientInstanceId,
			requestId,
			command: { kind: "init", opts: buildInitOpts(includeHostActions) },
		});
		enqueue(envelope);
		return envelope.requestId;
	};

	const runAfterRegistrationProbe = (run: () => void): void => {
		const registrationProbe = pendingRegistrationProbe;
		if (!registrationProbe) {
			run();
			return;
		}
		void registrationProbe.then(run);
	};

	const enqueueSetDefaultContainerPolicy = (policy: ContainerPolicy): void => {
		runAfterRegistrationProbe(() => {
			enqueue(
				createCommandEnvelope({
					command: { kind: "setDefaultContainerPolicy", policy },
				}),
			);
		});
	};

	const enqueueSetContainer = (
		flowHandleId: FlowHandleId,
		element: HTMLElement | null,
	): void => {
		enqueue(
			createCommandEnvelope({
				command: { kind: "setContainer", flowHandleId, container: element },
			}),
		);
	};

	const reportAsyncError = <T>(promise: Promise<T>): Promise<T> => {
		return promise.catch((error) => {
			notifyError(error);
			throw error;
		});
	};

	const ensureCommandsCanRun = (): void => {
		if (!hostWindow) {
			return;
		}
		if (!isLoaderRequested()) {
			if (disableAutoLoadConfig) {
				throw new Error("Widget not loaded. Call client.load() first.");
			}
			load();
		}
	};

	const assertInlineFlowRuntimeSupport = (): void => {
		const runtimeMarker = hostWindow?.[LOADER_RUNTIME_GLOBAL_KEY];
		if (!runtimeMarker?.alive) {
			throw new Error(
				"Inline flows require an active compatible Loader runtime.",
			);
		}
		if (runtimeMarker.protocolVersion !== LOADER_RUNTIME_PROTOCOL_VERSION) {
			throw new Error(
				`Incompatible loader runtime protocol: expected ${LOADER_RUNTIME_PROTOCOL_VERSION}, received ${runtimeMarker.protocolVersion ?? "unknown"}`,
			);
		}
		if (!runtimeMarker.features?.includes(UNIFIED_FLOW_RUNTIME_V1_CAPABILITY)) {
			throw new Error(
				`Inline flows require Loader feature ${UNIFIED_FLOW_RUNTIME_V1_CAPABILITY}.`,
			);
		}
	};

	/** Enqueue a command and await its settlement.
	 *  The loader resolves instanceId from the instance registry. */
	const rawSettlementDispatch = createCommandSettlementDispatch((cmd) =>
		enqueue(cmd),
	);
	// createCommandSettlementDispatch inserts the envelope synchronously before
	// returning its settlement promise, which makes invocation the handoff edge.
	const handOffCommand = (input: Command) => rawSettlementDispatch(input);
	const handOffInlineFlowCommand = (
		input: InternalHostInlineFlowCommandPayload,
	) => {
		const requestId = createCommandRequestId();
		enqueue({
			version: "1",
			requestId,
			idempotencyKey: requestId,
			command: input,
		});
		return waitForCommandSettlement({
			requestId,
			windowRef: hostWindow ?? undefined,
		});
	};
	const transportAdmission = createClientTransportAdmission();
	const prepareCommandHandoff = (): void | Promise<void> => {
		ensureCommandsCanRun();
		const registrationProbe = pendingRegistrationProbe;
		if (registrationProbe) {
			return registrationProbe;
		}
	};
	const prepareInlineFlowCommandHandoff = (): void | Promise<void> => {
		ensureCommandsCanRun();
		const startup = pendingLoad ?? pendingRegistrationProbe;
		if (startup) {
			return startup.then(assertInlineFlowRuntimeSupport);
		}
		assertInlineFlowRuntimeSupport();
	};
	const dispatch = async (input: Command): Promise<void> => {
		const preparation = prepareCommandHandoff();
		if (preparation) {
			await preparation;
		}
		await rawSettlementDispatch(input);
	};

	const enqueueConfigUpdate = (opts: ConfigureOptions): void => {
		if (!isLoaderRequested()) {
			return;
		}
		runAfterRegistrationProbe(() => {
			enqueue(
				createCommandEnvelope({
					command: { kind: "configure", opts },
				}),
			);
		});
	};

	const toConfigureCommandOptions = (
		updates: ConfigureOptions,
	): ConfigureOptions => {
		const nextCapabilities = toCapabilities(updates.capabilities);
		return {
			...(updates.colorScheme !== undefined && {
				colorScheme: updates.colorScheme,
			}),
			...(updates.consent !== undefined && { consent: updates.consent }),
			...(updates.auth !== undefined && { auth: updates.auth }),
			...(nextCapabilities !== undefined && {
				capabilities: nextCapabilities,
			}),
		};
	};

	const updateOptions = (
		next: ClientOptions,
		nextActionRegistry: ActionRegistry,
		nextLinkRouter: LinkRouter | undefined,
	): void => {
		const linkRouterOwnershipChanged =
			(linkRouterConfig === undefined) !== (nextLinkRouter === undefined);
		if (
			linkRouterOwnershipChanged &&
			(isLoaderRequested() || pendingLoad !== null)
		) {
			throw new Error(
				"links.router cannot be added or removed after widget init; keeping original router",
			);
		}
		const definitionsChanged = !haveSameActionDefinitions(
			actionRegistry.listRegistrationIdentities(),
			nextActionRegistry.listRegistrationIdentities(),
		);
		if (definitionsChanged && (isLoaderRequested() || pendingLoad !== null)) {
			const error = new Error(
				"actions definitions cannot be changed after widget init; keeping original registrations",
			);
			notifyError(error);
			throw error;
		}
		actionRegistry = nextActionRegistry;
		linkRouterConfig = nextLinkRouter;
		disableAutoLoadConfig = next.disableAutoLoad ?? disableAutoLoadConfig;
		defaultConsentConfig = next.defaultConsent ?? defaultConsentConfig;
		if (!isLoaderRequested() && pendingLoad === null) {
			if (next.colorScheme !== undefined) {
				colorSchemeConfig = next.colorScheme;
			}
			if (next.flags !== undefined) {
				flagsConfig = toFlags(next.flags);
			}
			if (next.capabilities !== undefined) {
				capabilitiesConfig = toCapabilities(next.capabilities);
			}
		}

		if (next._loaderUrl !== undefined && next._loaderUrl !== loaderUrlConfig) {
			if (isLoaderRequested() || pendingLoad !== null) {
				const error = new Error(
					"_loaderUrl cannot be changed after widget init; keeping original",
				);
				notifyError(error);
			} else {
				loaderUrlConfig = next._loaderUrl;
			}
		}

		if (next._coreUrl !== undefined && next._coreUrl !== coreUrlConfig) {
			if (isLoaderRequested() || pendingLoad !== null) {
				const error = new Error(
					"_coreUrl cannot be changed after widget init; keeping original",
				);
				notifyError(error);
			} else {
				coreUrlConfig = next._coreUrl;
			}
		}

		if (next._apiUrl !== undefined && next._apiUrl !== apiUrlConfig) {
			if (isLoaderRequested() || pendingLoad !== null) {
				const error = new Error(
					"_apiUrl cannot be changed after widget init; keeping original",
				);
				notifyError(error);
			} else {
				apiUrlConfig = next._apiUrl;
			}
		}

		if (
			next._realtimeUrl !== undefined &&
			next._realtimeUrl !== realtimeUrlConfig
		) {
			if (isLoaderRequested() || pendingLoad !== null) {
				const error = new Error(
					"_realtimeUrl cannot be changed after widget init; keeping original",
				);
				notifyError(error);
			} else {
				realtimeUrlConfig = next._realtimeUrl;
			}
		}

		applyStartupConfig(next);

		if (!disableAutoLoadConfig && !isLoaderRequested()) {
			load();
		}
	};

	const applyStartupConfig = (next: ClientOptions): void => {
		// Send configure payload from next (colorScheme only from ClientOptions).
		if (!isLoaderRequested()) return;
		const opts: ConfigureOptions = {};
		if (next.colorScheme !== undefined) opts.colorScheme = next.colorScheme;
		if (next.capabilities !== undefined) {
			capabilitiesConfig = toCapabilities(next.capabilities);
			opts.capabilities = capabilitiesConfig;
		}
		enqueueConfigUpdate(opts);
	};

	const load = (): void => {
		if (!hostWindow || !hostDocument) {
			return;
		}
		if (isLoaderRequested() || pendingLoad) {
			return;
		}
		const runtimeUrls = resolveRequiredRuntimeUrls();

		const appendLoaderScript = (): void => {
			const scriptUrl = runtimeUrls.loaderUrl;
			if (
				!hostDocument.querySelector(`link[href="${scriptUrl}"][rel="preload"]`)
			) {
				const preload = hostDocument.createElement("link");
				preload.rel = "preload";
				preload.as = "script";
				preload.href = scriptUrl;
				hostDocument.head.appendChild(preload);
			}

			const script = hostDocument.createElement("script");
			script.src = scriptUrl;
			script.async = true;
			if (script.dataset) {
				script.dataset.disableAutoRegister = "true";
			} else if (typeof script.setAttribute === "function") {
				script.setAttribute("data-disable-auto-register", "true");
			}
			hostDocument.head.appendChild(script);
		};

		const runtimeMarker = hostWindow[LOADER_RUNTIME_GLOBAL_KEY];
		if (runtimeMarker?.alive) {
			const protocolVersion = runtimeMarker.protocolVersion;
			if (protocolVersion !== LOADER_RUNTIME_PROTOCOL_VERSION) {
				throw new Error(
					`Incompatible loader runtime protocol: expected ${LOADER_RUNTIME_PROTOCOL_VERSION}, received ${protocolVersion ?? "unknown"}`,
				);
			}
			const requestId = enqueueInstanceRegistration();
			loaderRequested = true;
			if (
				!runtimeMarker.features?.includes(UNIFIED_FLOW_RUNTIME_V1_CAPABILITY)
			) {
				let trackedPending: Promise<void>;
				const pending = waitForCommandSettlement({
					requestId,
					windowRef: hostWindow,
					timeoutMs: LOADER_RUNTIME_POST_LOAD_TIMEOUT_MS,
				})
					.then(() => {})
					.catch((error) => {
						notifyError(error);
					});
				trackedPending = pending.finally(() => {
					if (pendingRegistrationProbe === trackedPending) {
						pendingRegistrationProbe = null;
					}
				});
				pendingRegistrationProbe = trackedPending;
			}
			return;
		}

		const hasCommandQueue = Array.isArray(hostWindow.__getuserfeedback_queue);
		if (!hasCommandQueue) {
			const requestId = enqueueInstanceRegistration();
			appendLoaderScript();
			loaderRequested = true;
			let trackedPending: Promise<void>;
			const pending = waitForCommandSettlement({
				requestId,
				windowRef: hostWindow,
				timeoutMs: LOADER_RUNTIME_POST_LOAD_TIMEOUT_MS,
			})
				.then(() => {})
				.catch((error) => {
					notifyError(error);
				});
			trackedPending = pending.finally(() => {
				if (pendingLoad === trackedPending) {
					pendingLoad = null;
				}
			});
			pendingLoad = trackedPending;
			return;
		}

		loaderRequested = true;

		let trackedPending: Promise<void>;
		const pending = (async () => {
			const requestId = createCommandRequestId();
			enqueueInstanceRegistration(requestId);
			try {
				await waitForCommandSettlement({
					requestId,
					windowRef: hostWindow,
					timeoutMs: LOADER_RUNTIME_PROBE_TIMEOUT_MS,
				});
				return;
			} catch (error) {
				if (error instanceof CommandSettlementTimeoutError) {
					appendLoaderScript();
					await waitForCommandSettlement({
						requestId,
						windowRef: hostWindow,
						timeoutMs: LOADER_RUNTIME_POST_LOAD_TIMEOUT_MS,
					});
					return;
				}
				if (
					error instanceof CommandSettlementError &&
					error.code === INVALID_COMMAND_ERROR_CODE &&
					actionRegistry.listDefinitions().length > 0
				) {
					const fallbackRequestId = createCommandRequestId();
					enqueueInstanceRegistration(fallbackRequestId, false);
					await waitForCommandSettlement({
						requestId: fallbackRequestId,
						windowRef: hostWindow,
						timeoutMs: LOADER_RUNTIME_POST_LOAD_TIMEOUT_MS,
					});
					return;
				}
				throw error;
			}
		})().catch((error) => {
			notifyError(error);
		});
		trackedPending = pending.finally(() => {
			if (pendingLoad === trackedPending) {
				pendingLoad = null;
			}
			if (pendingRegistrationProbe === trackedPending) {
				pendingRegistrationProbe = null;
			}
		});
		pendingLoad = trackedPending;
		if (actionRegistry.listDefinitions().length > 0) {
			pendingRegistrationProbe = trackedPending;
		}
	};

	const subscribeFlowStateForHandle = (
		flowHandleId: FlowHandleId,
		callback: FlowStateCallback,
		options?: SubscribeFlowStateOptions,
	): (() => void) => {
		if (options?.emitInitial ?? true) {
			callback(getFlowStateForHandle(flowHandleId));
		}
		return hostEventHub.subscribeFlowStateForHandle(
			flowHandleId,
			(detail): void => {
				const nextState: FlowState = {
					isOpen: detail.isOpen,
					isLoading: detail.isLoading,
					width: detail.width,
					height: detail.height,
				};
				setFlowStateForHandle(flowHandleId, nextState);
				callback(cloneFlowState(nextState));
			},
			{ signal: options?.signal },
		);
	};

	const subscribeFlowStateForInstance = (
		callback: FlowStateCallback,
		options?: SubscribeFlowStateOptions,
	): (() => void) => {
		if (options?.emitInitial ?? true) {
			callback(getInstanceFlowState());
		}
		return hostEventHub.subscribeInstanceFlowState(
			(detail): void => {
				const nextState: FlowState = {
					isOpen: detail.isOpen,
					isLoading: detail.isLoading,
					width: detail.width,
					height: detail.height,
				};
				callback(cloneFlowState(nextState));
			},
			{ signal: options?.signal },
		);
	};

	const resetFlowRunsForClientBarrier = new Set<() => void>();
	let deferredFlowRunNotifications: Array<() => void> | null = null;

	function createFlowRun(target: { flowId: string }): FlowRun;
	function createFlowRun(target: { flow: InlineFlow }): InlineFlowRun;
	function createFlowRun(target: FlowRunTarget): FlowRun | InlineFlowRun {
		const openRequestController = createFlowOpenRequestController();
		let latestContainer: HTMLElement | null | undefined;
		let unsubscribeFlowStateCache: (() => void) | null = null;
		type SubscriberEntry = {
			callback: FlowStateCallback;
			options?: SubscribeFlowStateOptions;
			unsubscribe: (() => void) | null;
		};
		const activeSubscribers = new Set<SubscriberEntry>();
		const handleClient = createFlowCommandHandleController<
			FlowHandleId,
			{
				ok: boolean;
				kind: string;
				result?: unknown;
			}
		>({
			getHandleIdFromSettlement: (settlement) => {
				const handle = getFlowHandleFromSettlementDetail(settlement);
				return handle?.flowHandleId;
			},
			isStaleHandleError,
			onClearResolvedHandle: (previousFlowHandleId) => {
				claimedFlowStateHandles.delete(previousFlowHandleId);
				pendingFlowStateHandles.delete(previousFlowHandleId);
				if (unsubscribeFlowStateCache) {
					unsubscribeFlowStateCache();
					unsubscribeFlowStateCache = null;
				}
				setFlowStateForHandle(previousFlowHandleId, DEFAULT_FLOW_STATE);
				rebindSubscribersToCurrentHandle();
				const subscribersAtReset = [...activeSubscribers];
				const notify = (): void =>
					notifySubscribersWithDefaultState(subscribersAtReset);
				if (deferredFlowRunNotifications) {
					deferredFlowRunNotifications.push(notify);
				} else {
					notify();
				}
			},
		});

		const bindSubscriberToCurrentHandle = (
			subscriber: SubscriberEntry,
			emitCurrent = false,
		): void => {
			if (subscriber.unsubscribe) {
				subscriber.unsubscribe();
				subscriber.unsubscribe = null;
			}
			if (subscriber.options?.signal?.aborted) {
				activeSubscribers.delete(subscriber);
				return;
			}
			const flowHandleId = handleClient.getHandleId();
			if (!flowHandleId) {
				return;
			}
			const notifySubscriber = (state: FlowState): void => {
				try {
					subscriber.callback(state);
				} catch {
					// Public observers cannot interrupt handle publication or prevent
					// the remaining lifecycle projections from binding.
				}
			};
			const unsubscribe = subscribeFlowStateForHandle(
				flowHandleId,
				notifySubscriber,
				emitCurrent
					? { ...subscriber.options, emitInitial: true }
					: subscriber.options,
			);
			if (subscriber.options?.signal?.aborted) {
				unsubscribe();
				activeSubscribers.delete(subscriber);
				return;
			}
			if (
				handleClient.getHandleId() !== flowHandleId ||
				!activeSubscribers.has(subscriber)
			) {
				unsubscribe();
				return;
			}
			subscriber.unsubscribe = unsubscribe;
		};

		const rebindSubscribersToCurrentHandle = (emitCurrent = false): void => {
			for (const subscriber of activeSubscribers) {
				bindSubscriberToCurrentHandle(subscriber, emitCurrent);
			}
		};

		const notifySubscribersWithDefaultState = (
			subscribersAtReset: readonly SubscriberEntry[],
		): void => {
			for (const subscriber of subscribersAtReset) {
				if (!activeSubscribers.has(subscriber)) continue;
				try {
					subscriber.callback(cloneFlowState(DEFAULT_FLOW_STATE));
				} catch {
					// A subscriber cannot prevent later reset observers from seeing the
					// already-completed client-wide transition.
				}
			}
		};

		const rebindFlowStateCache = (): void => {
			if (unsubscribeFlowStateCache) {
				unsubscribeFlowStateCache();
				unsubscribeFlowStateCache = null;
			}
			const flowHandleId = handleClient.getHandleId();
			if (!flowHandleId) {
				return;
			}
			unsubscribeFlowStateCache =
				subscribeFlowStateCacheForHandle(flowHandleId);
		};

		const clearOpenIntent = (): void => {
			openRequestController.clear();
		};

		const flushPendingForResolvedHandle = (): void => {
			const flowHandleId = handleClient.getHandleId();
			if (!flowHandleId) {
				return;
			}
			const shouldReplayPendingState =
				pendingFlowStateHandles.delete(flowHandleId);
			claimedFlowStateHandles.add(flowHandleId);
			ensureFlowStateForHandle(flowHandleId);
			if (latestContainer !== undefined) {
				enqueueSetContainer(flowHandleId, latestContainer);
			}
			rebindSubscribersToCurrentHandle(shouldReplayPendingState);
			rebindFlowStateCache();
		};

		handleClient.subscribeHandle(() => {
			flushPendingForResolvedHandle();
		});
		subscribeFlowHandleInvalidation((flowHandleId) => {
			handleClient.invalidateResolvedHandle(flowHandleId);
		});
		type HandleCommandPayload =
			| Extract<
					Command,
					{ kind: "open" } | { kind: "prefetch" } | { kind: "prerender" }
			  >
			| InternalHostInlineFlowCommandPayload;

		const handOffHandleCommand = (payload: HandleCommandPayload) =>
			"flow" in payload
				? handOffInlineFlowCommand(payload)
				: handOffCommand(payload);

		const enqueueAndWaitForSettlement = async (
			buildPayload: (
				flowHandleId: FlowHandleId | undefined,
			) => HandleCommandPayload,
		): Promise<"settled" | "superseded"> => {
			const result = await transportAdmission.runOperation(
				({ attemptHandoff }) => {
					let releaseFlowHandleAllocation: (() => void) | null = null;
					return handleClient
						.runCommand(buildPayload, (payload) => {
							const isHandleFree =
								!("flowHandleId" in payload) ||
								payload.flowHandleId === undefined;
							const requiresSerializedAllocation =
								"flow" in target && isHandleFree;
							const handOff = (): Promise<{
								ok: boolean;
								kind: string;
								result?: unknown;
							}> =>
								attemptHandoff({
									prepare:
										"flow" in target
											? prepareInlineFlowCommandHandoff
											: prepareCommandHandoff,
									handOff: () => ({
										settlement: handOffHandleCommand(payload),
									}),
								});
							if (!requiresSerializedAllocation) {
								return handOff();
							}
							const handOffAllocation = () =>
								handOff().catch((error) => {
									clearPendingFlowHandleState();
									throw error;
								});
							if (releaseFlowHandleAllocation) {
								return handOffAllocation();
							}
							const acquisition = acquireFlowHandleAllocation();
							if (typeof acquisition === "function") {
								releaseFlowHandleAllocation = acquisition;
								beginFlowHandleAllocation();
								return handOffAllocation();
							}
							return acquisition.then((release) => {
								releaseFlowHandleAllocation = release;
								beginFlowHandleAllocation();
								return handOffAllocation();
							});
						})
						.finally(() => {
							if (releaseFlowHandleAllocation) {
								endFlowHandleAllocation();
								releaseFlowHandleAllocation();
							}
						});
				},
			);
			return result.status;
		};

		const openFlow = (options?: OpenFlowOptions): Promise<void> => {
			return openRequestController.run(() =>
				reportAsyncError(
					enqueueAndWaitForSettlement((flowHandleId) =>
						"flow" in target
							? {
									kind: "open",
									flow: target.flow,
									...(flowHandleId === undefined ? {} : { flowHandleId }),
									...(flowHandleId === undefined &&
									latestContainer !== undefined
										? { container: null }
										: {}),
									metadata: options?.metadata,
									hideCloseButton: options?.hideCloseButton,
								}
							: {
									kind: "open",
									flowId: target.flowId,
									flowHandleId,
									container: options?.container,
									metadata: options?.metadata,
									hideCloseButton: options?.hideCloseButton,
								},
					).then(() => {}),
				),
			);
		};

		const resetForClientBarrier = (): void => {
			clearOpenIntent();
			handleClient.reset();
		};
		resetFlowRunsForClientBarrier.add(resetForClientBarrier);

		const run: InlineFlowRun = {
			open: openFlow,
			prefetch: () => {
				return reportAsyncError(
					enqueueAndWaitForSettlement((flowHandleId) =>
						"flow" in target
							? {
									kind: "prefetch",
									flow: target.flow,
									...(flowHandleId === undefined ? {} : { flowHandleId }),
								}
							: {
									kind: "prefetch",
									flowId: target.flowId,
									flowHandleId,
								},
					).then(() => {}),
				);
			},
			prerender: (options?: PrerenderFlowOptions) => {
				return reportAsyncError(
					enqueueAndWaitForSettlement((flowHandleId) =>
						"flow" in target
							? {
									kind: "prerender",
									flow: target.flow,
									...(flowHandleId === undefined ? {} : { flowHandleId }),
									hideCloseButton: options?.hideCloseButton,
								}
							: {
									kind: "prerender",
									flowId: target.flowId,
									flowHandleId,
									hideCloseButton: options?.hideCloseButton,
								},
					).then(() => {}),
				);
			},
			close: () => {
				clearOpenIntent();
				return reportAsyncError(
					transportAdmission
						.runOperation(({ attemptHandoff }) => {
							return handleClient.runResolvedHandleCommand(
								(flowHandleId) => ({
									kind: "close" as const,
									flowHandleId,
								}),
								(payload) =>
									attemptHandoff({
										prepare:
											"flow" in target
												? prepareInlineFlowCommandHandoff
												: prepareCommandHandoff,
										handOff: () => ({
											settlement: handOffCommand(payload).then(() => {}),
										}),
									}),
							);
						})
						.then(() => {}),
				);
			},
			setContainer: (element: HTMLElement | null) => {
				latestContainer = element;
				const flowHandleId = handleClient.getHandleId();
				if (!flowHandleId) {
					return;
				}
				enqueueSetContainer(flowHandleId, element);
			},
			getFlowState: () => {
				const flowHandleId = handleClient.getHandleId();
				return flowHandleId
					? getFlowStateForHandle(flowHandleId)
					: cloneFlowState(DEFAULT_FLOW_STATE);
			},
			subscribeFlowState: (
				callback: FlowStateCallback,
				options?: SubscribeFlowStateOptions,
			) => {
				if (options?.signal?.aborted) {
					return () => {};
				}
				const subscriber: SubscriberEntry = {
					callback,
					options,
					unsubscribe: null,
				};
				activeSubscribers.add(subscriber);
				const unsubscribeSubscriber = (): void => {
					if (subscriber.unsubscribe) {
						subscriber.unsubscribe();
						subscriber.unsubscribe = null;
					}
					activeSubscribers.delete(subscriber);
				};
				const flowHandleId = handleClient.getHandleId();
				if (!flowHandleId) {
					const signal = options?.signal;
					if (signal) {
						const onAbort = (): void => {
							subscriber.unsubscribe = null;
							activeSubscribers.delete(subscriber);
						};
						signal.addEventListener("abort", onAbort, { once: true });
						subscriber.unsubscribe = () => {
							signal.removeEventListener("abort", onAbort);
						};
					}
					if (options?.emitInitial !== false) {
						callback(cloneFlowState(DEFAULT_FLOW_STATE));
					}
					return unsubscribeSubscriber;
				}
				bindSubscriberToCurrentHandle(subscriber);
				return unsubscribeSubscriber;
			},
		};

		return "flow" in target ? run : { flowId: target.flowId, ...run };
	}

	const flowRunByFlowId = new Map<string, FlowRun>();
	const flow = (flowIdInput: string): FlowRun => {
		const flowId = flowIdInput.trim();
		if (!flowId) {
			throw new Error("flowId must be a non-empty string");
		}
		const existing = flowRunByFlowId.get(flowId);
		if (existing) return existing;
		const run = createFlowRun({ flowId });
		flowRunByFlowId.set(flowId, run);
		return run;
	};

	const runClientBarrier = (kind: "close" | "reset"): Promise<void> => {
		const flowRunResets = [...resetFlowRunsForClientBarrier];
		return reportAsyncError(
			transportAdmission.runBarrier({
				onGenerationAdvanced: () => {
					const notifications: Array<() => void> = [];
					deferredFlowRunNotifications = notifications;
					try {
						for (const resetFlowRun of flowRunResets) {
							resetFlowRun();
						}
					} finally {
						deferredFlowRunNotifications = null;
					}
					for (const notify of notifications) {
						notify();
					}
				},
				prepare: hostWindow ? prepareCommandHandoff : () => {},
				handOff: hostWindow
					? () => ({ settlement: handOffCommand({ kind }).then(() => {}) })
					: () => ({ settlement: Promise.resolve() }),
			}),
		);
	};

	const close = (): Promise<void> => runClientBarrier("close");

	const reset = (): Promise<void> => runClientBarrier("reset");

	function identify(
		userId: string,
		traits?: IdentifyTraits,
		options?: IdentifyOptions,
	): Promise<void>;
	function identify(
		traits: IdentifyTraits,
		options?: IdentifyOptions,
	): Promise<void>;
	function identify(
		identifyInput: string | IdentifyTraits,
		traitsOrOptions?: IdentifyTraits | IdentifyOptions,
		options?: IdentifyOptions,
	): Promise<void> {
		if (!hostWindow) {
			return Promise.resolve();
		}
		return reportAsyncError(
			dispatch(
				toIdentifyCommandPayload(identifyInput, traitsOrOptions, options),
			),
		);
	}

	const track = (
		eventName: string,
		properties?: EventProperties,
		options?: EventOptions,
	): Promise<void> => {
		if (!hostWindow) {
			parseCustomerOccurrenceName(eventName, "eventName");
			return Promise.resolve();
		}
		return reportAsyncError(
			dispatch(
				buildCustomerOccurrenceCommand({
					type: "track",
					event: eventName,
					properties,
					options,
				}),
			),
		);
	};

	const page = (
		name?: string,
		properties?: EventProperties,
		options?: EventOptions,
	): Promise<void> => {
		if (!hostWindow) {
			if (name !== undefined) {
				parseCustomerOccurrenceName(name, "pageName");
			}
			return Promise.resolve();
		}
		return reportAsyncError(
			dispatch(
				buildCustomerOccurrenceCommand({
					type: "page",
					event:
						name === undefined ? hostWindow.location.pathname || "/" : name,
					properties,
					options,
				}),
			),
		);
	};

	const submitFeedback = async (
		input: FeedbackInput,
	): Promise<SubmitFeedbackResult> => {
		if (!hostWindow) {
			throw new Error("Feedback submission requires a browser environment.");
		}
		ensureCommandsCanRun();
		const registrationProbe = pendingRegistrationProbe;
		if (registrationProbe) {
			await registrationProbe;
		}
		const settlement = await reportAsyncError(
			rawSettlementDispatch({
				kind: "instanceFeedback.capture",
				payload: input,
			}),
		);
		if (!settlement.ok) {
			throw new Error("Feedback submission failed.");
		}
		const result = getFeedbackSubmissionFromSettlementDetail(settlement);
		if (!result) {
			throw new Error("Invalid feedback submission result.");
		}
		return result;
	};

	const fetchFeedback = async (
		input: FetchFeedbackInput,
	): Promise<FetchFeedbackResult> => {
		if (!hostWindow) {
			throw new Error("Feedback reads require a browser environment.");
		}
		ensureCommandsCanRun();
		const registrationProbe = pendingRegistrationProbe;
		if (registrationProbe) {
			await registrationProbe;
		}
		const settlement = await reportAsyncError(
			rawSettlementDispatch({
				kind: "instanceFeedback.listMine",
				payload: input,
			}),
		);
		if (!settlement.ok) {
			throw new Error("Feedback read failed.");
		}
		const result = getFeedbackReadFromSettlementDetail(settlement);
		if (!result) {
			throw new Error("Invalid feedback read result.");
		}
		return result;
	};

	const configure = (updates: ConfigureOptions): Promise<void> => {
		if (!hostWindow) {
			return Promise.resolve();
		}
		if (!isLoaderRequested()) {
			return Promise.resolve();
		}
		// Send updates as-is; loader merges and forwards deltas to core.
		return reportAsyncError(
			dispatch({ kind: "configure", opts: toConfigureCommandOptions(updates) }),
		);
	};

	hostEventHub.subscribeOpenRequested((detail, hostEvent) => {
		openRequestBridge.observeFromLoader(detail, {
			preventHostDefault: () => {
				hostEvent.preventDefault();
			},
		});
	});
	hostEventHub.subscribeHostActionRequested((detail, hostEvent) => {
		const handler = actionRegistry.getHandler(detail.definition);
		if (!handler) {
			detail.settle("unavailable");
			return;
		}
		// This adapter event stays inside the trusted host realm. Sandboxed Views
		// can request an action only through Core's empty, authorized RPC.
		hostEvent.preventDefault();
		void Promise.resolve()
			.then(() => handler())
			.then(
				() => detail.settle("success"),
				() => detail.settle("failure"),
			);
	});

	const client: Client = {
		load,
		setDefaultContainerPolicy: (policy: ContainerPolicy) => {
			enqueueSetDefaultContainerPolicy(policy);
		},
		getFlowState: () => getInstanceFlowState(),
		subscribeFlowState: (
			callback: FlowStateCallback,
			options?: SubscribeFlowStateOptions,
		) => subscribeFlowStateForInstance(callback, options),
		flow,
		close,
		reset,
		configure,
		identify,
		track,
		page,
		graph: {
			connect: (relationship) =>
				track("gx.graph.relationship.connected", relationship),
			disconnect: (relationship) =>
				track("gx.graph.relationship.disconnected", relationship),
		},
		feedback: { fetch: fetchFeedback, submit: submitFeedback },
		onOpenRequested: (callback) => openRequestBridge.subscribe(callback),
	};
	attachInlineFlowClientBridge(client, {
		createInlineFlowRun: (inlineFlow) => createFlowRun({ flow: inlineFlow }),
	});

	openRequestBridgeByClient.set(client, openRequestBridge);

	registry.set(registryKey, {
		client,
		updateOptions,
	});

	if (!disableAutoLoadConfig) {
		client.load();
	}

	return client;
};
